import type { FormField } from "@/api/cashier";
import type { FieldOptionsFilter } from "@/components/forms/dynamic-form/DynamicForm";

/**
 * Builds a {@link FieldOptionsFilter} from the `option_constraints` declared on the
 * given form fields. The constraints are the single source of truth defined in the
 * payment method YAML config and exposed through the API, so frontend filtering and
 * backend validation can never drift.
 *
 * For a field that declares `option_constraints`, its options are filtered to those
 * allowed by the current value of its controlling field (forward direction). The
 * controlling field is, in turn, guided by the currently selected dependent value
 * (reverse direction) — but only until the controlling field is chosen, after which
 * it stays fully re-selectable.
 *
 * Returns `undefined` when no field declares constraints, so callers can skip
 * wiring up a filter entirely.
 */
export const createOptionConstraintsFilter = (
  fields: FormField[]
): FieldOptionsFilter | undefined => {
  const constrained = fields.filter((field) => field.option_constraints);
  if (constrained.length === 0) return undefined;

  return (fieldId, options, formData) => {
    // Forward: this field's options depend on the value of its controlling field.
    const own = constrained.find(
      (field) => field.id === fieldId
    )?.option_constraints;
    if (own) {
      const controllingValue = formData[own.field];
      if (!controllingValue) return options;

      const allowed = own.allowed[controllingValue];
      if (!allowed) return options;

      return options.filter((opt) => allowed.includes(opt.id));
    }

    // Reverse: this field controls a dependent field, so guide its options by the
    // dependent selection — but only until this field has been chosen.
    const dependent = constrained.find(
      (field) => field.option_constraints?.field === fieldId
    );
    if (dependent?.option_constraints) {
      if (formData[fieldId]) return options;

      const selectedDependent = formData[dependent.id];
      if (!selectedDependent) return options;

      const allowedControlling = Object.entries(
        dependent.option_constraints.allowed
      )
        .filter(([, values]) => values.includes(selectedDependent))
        .map(([controllingValue]) => controllingValue);

      if (allowedControlling.length === 0) return options;

      return options.filter((opt) => allowedControlling.includes(opt.id));
    }

    return options;
  };
};
